import { Injectable } from '@angular/core';
import { async, Subject } from 'rxjs';

// Internal models
import { ReservationByDefaultBayIdResponse } from 'src/app/models/in-bay-management/responses/reservaitonByDefaultBayId.response';
import { CorporationDetailDto } from 'src/app/models/venue/public-corporation-detail.dto';
import { AllReservationGuestsRequirementsResponse } from 'src/app/models/in-bay-management/responses/allReservationGuestsRequirements.response';

@Injectable({
  providedIn: 'root'
})
export class InBayProvider {
    // route variables
    defaultBayId: string;
    corporationDetailId: string;
    isTablet: boolean;

    // initially fetched data variables
    inBayDataLoadedObservable = new Subject<boolean>();
    stripePublishableKey: string;
    corporationDetails: CorporationDetailDto;
    reservationDetails: ReservationByDefaultBayIdResponse;
    reservationDetailsObservable = new Subject<ReservationByDefaultBayIdResponse>();
    allReservationGuestsRequirements: AllReservationGuestsRequirementsResponse;
    allReservationGuestsRequirementsObservable = new Subject<AllReservationGuestsRequirementsResponse>();

    // reservation has requirements due variables
    reservationHasRequirementsDue = false;
    reservationHasRequirementsDueObservable = new Subject<boolean>();
    
    // venue data variables
    venueTimeZoneId: string;
    corporationUserId: string;
    currentVenueTime: string;
    venueDataLoadedObservable = new Subject<boolean>();

    // increase bay time variables
    increaseBayTimeButtonDisabled = false;
    increaseBayTimeButtonDisabledObservable = new Subject<boolean>();

    constructor() { }

    // route variables
    async setRouteVariables(defaultBayId: string, corporationDetailId: string, isTablet: boolean) {
        this.defaultBayId = defaultBayId;
        this.corporationDetailId = corporationDetailId;
        this.isTablet = isTablet;
    }

    // ENG-754 — the token from an emailed verification link's URL fragment. Held in memory only, and
    // consumed once: the page strips it from the address bar as soon as it is read.
    private verificationToken: string | null = null;

    setVerificationToken(token: string | null) {
        this.verificationToken = token;
    }

    /** Returns the token and clears it, so a single link can never verify twice. */
    takeVerificationToken(): string | null {
        const token = this.verificationToken;
        this.verificationToken = null;
        return token;
    }

    async getRouteVariables() {
        return {
            defaultBayId: this.defaultBayId,
            corporationDetailId: this.corporationDetailId,
            isTablet: this.isTablet
        };
    }

    async getCorporationDetailId() {
        return this.corporationDetailId;
    }

    emitDataLoaded(
        stripePublishableKey: string,
        reservationDetails: ReservationByDefaultBayIdResponse,
        corporationDetails: CorporationDetailDto,
        dataLoaded: boolean
    ) {
        this.stripePublishableKey = stripePublishableKey;
        this.reservationDetails = reservationDetails;
        this.corporationDetails = corporationDetails;
        this.inBayDataLoadedObservable.next(dataLoaded);
    }

    async setReservationDetails(reservationDetails: ReservationByDefaultBayIdResponse | null) {
        this.reservationDetails = reservationDetails;
        this.reservationDetailsObservable.next(reservationDetails);
    }

    async getReservationDetails(): Promise<ReservationByDefaultBayIdResponse | null> {
        return this.reservationDetails;
    }

    // all reservation guests are members
    async setAllReservationGuestsRequirements(allReservationGuestsRequirements: AllReservationGuestsRequirementsResponse) {
        this.allReservationGuestsRequirements = allReservationGuestsRequirements;
        this.allReservationGuestsRequirementsObservable.next(allReservationGuestsRequirements);
    }

    // Venue data methods
    setVenueData(timeZoneId: string, corporationUserId: string, currentVenueTime: string) {
        this.venueTimeZoneId = timeZoneId;
        this.corporationUserId = corporationUserId;
        this.currentVenueTime = currentVenueTime;
        this.venueDataLoadedObservable.next(true);
    }
    
    getVenueTimeZoneId(): string {
        return this.venueTimeZoneId;
    }
    
    getCorporationUserId(): string {
        return this.corporationUserId;
    }
    
    getCurrentVenueTime(): string {
        return this.currentVenueTime;
    }

    async getAllReservationGuestsRequirements(): Promise<AllReservationGuestsRequirementsResponse> {
        return this.allReservationGuestsRequirements;
    }

    // reservation has requirements due status
    async setReservationHasRequirementsDue(reservationHasRequirementsDue: boolean) {
        this.reservationHasRequirementsDue = reservationHasRequirementsDue;
        this.reservationHasRequirementsDueObservable.next(reservationHasRequirementsDue);
    }

    async getReservationHasRequirementsDue(): Promise<boolean> {
        return this.reservationHasRequirementsDue;
    }

    // stripe functions
    emitIncreaseBayTimeButtonDisabled(disabled: boolean) {
        this.increaseBayTimeButtonDisabled = disabled;
        this.increaseBayTimeButtonDisabledObservable.next(disabled);
    }
}
